import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, ImageOff, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/** One selectable Google Places reference photo. */
export interface ReferencePhotoOption {
  /** Places photo resource name — posted back so the job uses THIS photo. */
  name: string;
  /** Directly renderable image URL. */
  url: string;
}

interface ReferencePhotosResponse {
  success: boolean;
  data: {
    placeName: string | null;
    photos: ReferencePhotoOption[];
    availableThemes: string[];
  };
}

/** Fallback theme offered while the options are loading / unavailable. */
const DEFAULT_THEME = 'default';

/** How many images can be generated per run (mirrors the API schema cap). */
const MAX_IMAGE_COUNT = 5;

/** "ghost_pub_crawl" → "Ghost pub crawl" for the theme picklist. */
function humanizeTheme(theme: string): string {
  const spaced = theme.replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export interface GenerateAttractionImagesDialogProps {
  attractionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called after the generation job is successfully kicked off (after the
   * success toast, before/while the dialog closes). Use it to refresh caches
   * that surface the attraction — e.g. invalidate the attraction detail query
   * so the featured image updates once the job lands. Optional so the gallery
   * usage, which fetches its own list, is unaffected.
   */
  onGenerated?: () => void;
}

/**
 * AI image generation dialog for a single attraction.
 *
 * Before anything is generated, the dialog fetches the attraction's Google
 * Places photos and asks the user to CONFIRM which photo the AI should use
 * as its reference. AI images are never generated without a real reference
 * photo — when Google finds no candidates the dialog shows an error and
 * generation is disabled (there is no text-only fallback).
 *
 * @param props - Attraction id and controlled open state.
 * @returns The dialog.
 */
export function GenerateAttractionImagesDialog({
  attractionId,
  open,
  onOpenChange,
  onGenerated,
}: GenerateAttractionImagesDialogProps) {
  const apiClient = useApiClient();

  const [selectedPhotoName, setSelectedPhotoName] = useState<string | null>(null);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [count, setCount] = useState(1);
  const [setAsFeatured, setSetAsFeatured] = useState(true);

  const { data, isLoading, error } = useQuery<ReferencePhotosResponse>({
    queryKey: ['Attraction', attractionId, 'reference-photos'],
    queryFn: () => apiClient.get(`/attractions/${attractionId}/reference-photos`),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const placeName = data?.data.placeName ?? null;
  const photos = data?.data.photos ?? [];
  const themes = data?.data.availableThemes ?? [DEFAULT_THEME];

  // Preselect the first photo once candidates arrive (the job's old default).
  useEffect(() => {
    if (open && photos.length > 0 && selectedPhotoName === null) {
      setSelectedPhotoName(photos[0].url ? photos[0].name : null);
    }
  }, [open, photos, selectedPhotoName]);

  const generate = useMutation({
    mutationFn: () =>
      apiClient.post(`/attractions/${attractionId}/generate-images`, {
        theme,
        count,
        setAsFeatured,
        addToGallery: true,
        ...(selectedPhotoName ? { referencePhotoName: selectedPhotoName } : {}),
      }),
    onSuccess: () => {
      toast.success(
        'Image generation started — new images will appear in the gallery in a minute or two.',
      );
      onGenerated?.();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to start image generation.');
    },
  });

  const selectedPhoto = photos.find((photo) => photo.name === selectedPhotoName) ?? null;
  // AI images require a real reference photo — no candidates means no generation.
  const noReferenceAvailable = Boolean(error) || photos.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Generate images with AI</DialogTitle>
          <DialogDescription>
            Confirm the reference photo first — the AI bases its image on the photo you pick.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Looking up Google photos…
          </div>
        ) : (
          <div className="space-y-5">
            {noReferenceAvailable ? (
              <div
                role="alert"
                className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-sm text-destructive"
              >
                <ImageOff className="mt-0.5 h-5 w-5 shrink-0" />
                <p>
                  No reference photo available. AI images require a real reference photo; check
                  the attraction name and city so Google can find it.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium">
                    Google matched: <span className="font-semibold">{placeName}</span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Is this the right place? Pick the photo the AI should work from.
                  </p>
                </div>

                <div className="space-y-3">
                  {selectedPhoto && (
                    <img
                      src={selectedPhoto.url}
                      alt={`Reference photo of ${placeName}`}
                      className="max-h-64 w-full rounded-lg border object-cover"
                    />
                  )}
                  {photos.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {photos.map((photo) => (
                        <button
                          key={photo.name}
                          type="button"
                          onClick={() => setSelectedPhotoName(photo.name)}
                          className={cn(
                            'relative h-16 w-24 shrink-0 overflow-hidden rounded-md border-2',
                            photo.name === selectedPhotoName
                              ? 'border-primary'
                              : 'border-transparent opacity-70 hover:opacity-100',
                          )}
                          aria-label="Select reference photo"
                        >
                          <img src={photo.url} alt="" className="h-full w-full object-cover" />
                          {photo.name === selectedPhotoName && (
                            <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                              <Check className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="image-theme">Theme</Label>
                <Select value={theme} onValueChange={setTheme}>
                  <SelectTrigger id="image-theme" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {themes.map((key) => (
                      <SelectItem key={key} value={key}>
                        {humanizeTheme(key)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="image-count">Images</Label>
                <Select value={String(count)} onValueChange={(value) => setCount(Number(value))}>
                  <SelectTrigger id="image-count" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: MAX_IMAGE_COUNT }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="set-featured"
                checked={setAsFeatured}
                onCheckedChange={setSetAsFeatured}
              />
              <Label htmlFor="set-featured" className="text-sm">
                Set the first image as the featured image
              </Label>
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => generate.mutate()}
            disabled={isLoading || generate.isPending || noReferenceAvailable || !selectedPhoto}
          >
            {generate.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting…
              </>
            ) : (
              'Use this photo & generate'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
